import React, {
    useEffect,
    useMemo,
    useState,
    useCallback,
} from "react";
import { gql, useQuery } from "@apollo/client";

import { GlobalStyles } from "../../constants/styles";
import InitialsAvatar from "../../util/name_initial";
import { SPORTS } from "../../constants/vars";
import useActiveProfile from "../common/hooks/useActiveProfile";
import useThemeColors from "../organization/portal/useThemeColors";
import { alphaFromHex } from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";

const GET_PEER_DIVISIONS = gql`
    query GetPeerDivisions(
        $divisionId: ID!
        $profileId: Int!
        $includeInactive: Boolean
        $sport: String
    ) {
        getPeerDivisions(
            divisionId: $divisionId
            profileId: $profileId
            includeInactive: $includeInactive
            sport: $sport
        ) {
            id
            name
            seasons {
                id
                name
                startDate
                endDate
                stages {
                    id
                    name
                    groups {
                        id
                        name
                    }
                }
                teams {
                    teamId
                    teamName
                    logo
                    team {
                        logoUrl
                        players {
                            id
                        }
                    }
                }
            }
        }
    }
`;

export default function AddExistingSeasonTeamsModal({
                                                        divisionId,
                                                        sport,
                                                        activeSeasonData,
                                                        currentSeasonTeams = [],
                                                        onConfirm,
                                                    }) {
    const colors = useThemeColors();
    const { t } = useTranslation();
    const modalId = "add-existing-season-teams-modal";

    const { activeProfile } = useActiveProfile();
    const profileId = activeProfile?.id || null;

    const translatedSportsList = useMemo(
        () => [
            { key: "", value: t("addExistingSeasonTeamsModal.allSports") },
            ...SPORTS.map((sport) => {
                const translated = t(`signUp.form.sports.${sport.key}`);
                return {
                    ...sport,
                    value:
                        translated && translated !== `sports.${sport.key}`
                            ? translated
                            : sport.value,
                };
            }),
        ],
        [t]
    );

    const [selectedSeasonId, setSelectedSeasonId] = useState("");
    const [selectedTeamIds, setSelectedTeamIds] = useState([]);
    const [keepPlayers, setKeepPlayers] = useState(false);
    const [carryPoints, setCarryPoints] = useState(false);
    const [deactivateSource, setDeactivateSource] = useState(false);
    const [sourceStageId, setSourceStageId] = useState("");
    const [sourceGroupId, setSourceGroupId] = useState("");
    const [targetStageId, setTargetStageId] = useState("");
    const [targetGroupId, setTargetGroupId] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [selectedDivisionId, setSelectedDivisionId] = useState(() =>
        divisionId ? String(divisionId) : ""
    );
    const [includeInactive, setIncludeInactive] = useState(false);
    const [selectedSportKey, setSelectedSportKey] = useState(sport || "");
    const effectiveKeepPlayers = keepPlayers || deactivateSource;

    const {
        data: peerDivisionsData,
        loading: peerDivisionsLoading,
        error: peerDivisionsError,
    } = useQuery(GET_PEER_DIVISIONS, {
        variables: {
            divisionId,
            profileId: profileId ?? 0,
            includeInactive,
            sport: selectedSportKey || null,
        },
        fetchPolicy: "network-only",
        skip: !divisionId || !profileId,
        onCompleted: (data) => {
            console.log("[AddExistingSeasonTeamsModal] GetPeerDivisions OK", data);
        },
        onError: (err) => {
            console.error("[AddExistingSeasonTeamsModal] GetPeerDivisions ERROR", err);
        },
    });

    const peerDivisions = useMemo(
        () => {
            const seenIds = new Set();
            return (peerDivisionsData?.getPeerDivisions || []).filter((division) => {
                const id = String(division.id);
                if (seenIds.has(id)) return false;
                seenIds.add(id);
                return true;
            });
        },
        [peerDivisionsData]
    );

    useEffect(() => {
        if (!peerDivisions.length) return;
        if (selectedDivisionId) return;

        const current = peerDivisions.find(
            (d) => String(d.id) === String(divisionId)
        );
        const first = current || peerDivisions[0];
        if (first) {
            setSelectedDivisionId(String(first.id));
        }
    }, [peerDivisions, selectedDivisionId, divisionId]);

    useEffect(() => {
        if (deactivateSource && !keepPlayers) {
            setKeepPlayers(true);
        }
    }, [deactivateSource, keepPlayers]);

    const selectedDivision = useMemo(
        () =>
            peerDivisions.find(
                (d) => String(d.id) === String(selectedDivisionId)
            ) || null,
        [peerDivisions, selectedDivisionId]
    );

    const seasons = useMemo(
        () => (selectedDivision?.seasons || []),
        [selectedDivision]
    );

    const modalStyles = useMemo(
        () => ({
            wrap: {
                borderRadius: 16,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 18px 44px rgba(0,0,0,0.18)",
            },
            header: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 14px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.50)},
                    ${alphaFromHex(colors.primary, 0.00)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
            },
            titleWrap: { display: "flex", alignItems: "center", gap: 10 },
            badge: {
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.04em",
                padding: "6px 10px",
                borderRadius: 999,
                color: colors.textOnPrimary,
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
                textTransform: "uppercase",
            },
            title: {
                margin: 0,
                fontSize: 16,
                fontWeight: 900,
                color: colors.textOnPrimary,
            },
            closeBtn: {
                width: 36,
                height: 36,
                borderRadius: 10,
                border: "1px solid rgba(0,0,0,0.10)",
                background: colors.primary,
                color: colors.textOnPrimary,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
            },
            body: {
                padding: 14,
                background: "#fff",
                maxHeight: "80vh",
                overflowY: "auto",
            },
            card: {
                borderRadius: 14,
                padding: 12,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.015)",
            },
            filtersRow: {
                display: "flex",
                flexWrap: "wrap",
                gap: 12,
                marginBottom: 12,
                alignItems: "center",
            },
            filterItem: {
                display: "flex",
                alignItems: "center",
                gap: 8,
            },
            sectionTitle: {
                margin: "2px 0 10px",
                fontSize: 13,
                fontWeight: 900,
                color: "rgba(20,21,26,0.78)",
                letterSpacing: "0.02em",
                textTransform: "uppercase",
            },
            label: {
                fontSize: 12.5,
                fontWeight: 800,
                color: "rgba(20,21,26,0.70)",
                marginBottom: 6,
            },
            input: {
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.12)",
                padding: "9px 12px",
                fontWeight: 800,
                outline: "none",
            },
            hint: {
                marginTop: 6,
                color: "rgba(20,21,26,0.55)",
                fontSize: 12.5,
                fontWeight: 600,
                lineHeight: 1.25,
            },
            footer: {
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 14px",
                background: "rgba(0,0,0,0.02)",
                borderTop: "1px solid rgba(0,0,0,0.06)",
            },
            btn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
            },
            btnGhost: {
                background: "#fff",
                border: "1px solid rgba(0,0,0,0.12)",
                color: "#14151a",
            },
            btnPrimary: {
                background: GlobalStyles.colors.canary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: GlobalStyles.colors.woodsmoke,
            },
            chip: {
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 999,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "rgba(var(--kbs-primary-rgb, 0,173,255), 0.22)",
                color: "#14151a",
                fontWeight: 900,
                fontSize: 12,
                whiteSpace: "nowrap",
            },
        }),
        [colors.primary, colors.textOnPrimary]
    );

    const existingTeamIds = useMemo(
        () => new Set((currentSeasonTeams || []).map((t) => String(t.teamId))),
        [currentSeasonTeams]
    );

    const defaultSeasonId = useMemo(() => {
        const list = seasons || [];
        const filtered = activeSeasonData?.id
            ? list.filter((s) => String(s.id) !== String(activeSeasonData.id))
            : list;

        if (!filtered.length) return "";
        const sorted = [...filtered].sort((a, b) => {
            const aDate = a?.startDate ? new Date(a.startDate) : new Date(0);
            const bDate = b?.startDate ? new Date(b.startDate) : new Date(0);
            return bDate - aDate;
        });
        return sorted[0]?.id ?? "";
    }, [seasons, activeSeasonData?.id]);

    useEffect(() => {
        setSelectedSeasonId(defaultSeasonId);
        setSelectedTeamIds([]);
        setKeepPlayers(false);
    }, [defaultSeasonId]);

    const selectedSeason = useMemo(
        () =>
            (seasons || []).find(
                (s) => String(s.id) === String(selectedSeasonId)
            ),
        [seasons, selectedSeasonId]
    );

    const teams = useMemo(() => selectedSeason?.teams ?? [], [selectedSeason]);

    const sourceStages = useMemo(
        () => selectedSeason?.stages ?? [],
        [selectedSeason]
    );

    const selectedSourceStage = useMemo(
        () =>
            sourceStages.find((stage) => String(stage.id) === String(sourceStageId)) ||
            null,
        [sourceStages, sourceStageId]
    );

    const sourceGroups = useMemo(
        () => selectedSourceStage?.groups ?? [],
        [selectedSourceStage]
    );

    const targetStages = useMemo(
        () => activeSeasonData?.stages ?? [],
        [activeSeasonData?.stages]
    );

    const selectedTargetStage = useMemo(
        () =>
            targetStages.find((stage) => String(stage.id) === String(targetStageId)) ||
            null,
        [targetStages, targetStageId]
    );

    const targetGroups = useMemo(
        () => selectedTargetStage?.groups ?? [],
        [selectedTargetStage]
    );

    useEffect(() => {
        const defaultStageId = sourceStages.length === 1 ? sourceStages[0].id : "";
        setSourceStageId(defaultStageId ? String(defaultStageId) : "");
        setSourceGroupId("");
    }, [selectedSeasonId, sourceStages]);

    useEffect(() => {
        const defaultStageId = targetStages.length === 1 ? targetStages[0].id : "";
        setTargetStageId(defaultStageId ? String(defaultStageId) : "");
        setTargetGroupId("");
    }, [activeSeasonData?.id, targetStages]);

    const selectableTeamIds = useMemo(
        () =>
            teams
                .filter((t) => !existingTeamIds.has(String(t.teamId)))
                .map((t) => t.teamId),
        [teams, existingTeamIds]
    );

    const isAllSelected =
        selectableTeamIds.length > 0 &&
        selectedTeamIds.length > 0 &&
        selectedTeamIds.length === selectableTeamIds.length;

    const toggleTeam = (teamId) => {
        if (existingTeamIds.has(String(teamId))) return;
        setSelectedTeamIds((prev) =>
            prev.includes(teamId)
                ? prev.filter((id) => id !== teamId)
                : [...prev, teamId]
        );
    };

    const onSelectAll = (checked) => {
        setSelectedTeamIds(checked ? selectableTeamIds : []);
    };

    const handleDivisionChange = (e) => {
        const newDivisionId = e.target.value;
        setSelectedDivisionId(newDivisionId);
        setSelectedSeasonId("");
        setSelectedTeamIds([]);
        setKeepPlayers(false);
        setCarryPoints(false);
        setDeactivateSource(false);
        setSourceStageId("");
        setSourceGroupId("");
    };

    const handleSeasonChange = (e) => {
        setSelectedSeasonId(e.target.value);
        setSelectedTeamIds([]);
        setKeepPlayers(false);
        setCarryPoints(false);
        setDeactivateSource(false);
        setSourceStageId("");
        setSourceGroupId("");
    };

    const handleIncludeInactiveChange = (e) => {
        setIncludeInactive(e.target.checked);
    };

    const handleSportChange = (e) => {
        setSelectedSportKey(e.target.value);
    };

    const closeModal = useCallback(() => {
        const el = document.getElementById(modalId);
        if (!el) return;

        if (window.$ && typeof window.$(el).modal === "function") {
            window.$(el).modal("hide");
            return;
        }

        const BS = window.bootstrap;
        const Modal = BS?.Modal;
        if (Modal) {
            try {
                const instance =
                    (typeof Modal.getInstance === "function" &&
                        Modal.getInstance(el)) ||
                    (typeof Modal.getOrCreateInstance === "function" &&
                        Modal.getOrCreateInstance(el)) ||
                    null;

                if (instance && typeof instance.hide === "function") {
                    instance.hide();
                    return;
                }

                const inst = new Modal(el);
                if (inst && typeof inst.hide === "function") {
                    inst.hide();
                    return;
                }
            } catch (e) {
            }
        }

        el.classList.remove("show");
        el.style.display = "none";
        el.setAttribute("aria-hidden", "true");

        document.body.classList.remove("modal-open");
        document.body.style.removeProperty("padding-right");
        document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
    }, [modalId]);

    const handleConfirm = async () => {
        if (!selectedSeasonId || !selectedTeamIds.length || !onConfirm) return;
        try {
            setIsSubmitting(true);
            await onConfirm({
                fromSeasonId: selectedSeasonId,
                teamIds: selectedTeamIds,
                keepPlayers: effectiveKeepPlayers,
                carryPoints,
                deactivateSource,
                sourceStageId: carryPoints ? sourceStageId : null,
                sourceGroupId: carryPoints ? sourceGroupId : null,
                targetStageId,
                targetGroupId,
            });
            setSelectedTeamIds([]);
            setKeepPlayers(false);
            setCarryPoints(false);
            setDeactivateSource(false);
            closeModal();
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <div
                className="modal fade"
                id={modalId}
                tabIndex="-1"
                role="dialog"
                aria-hidden="true"
            >
                <div
                    className="modal-dialog modal-lg modal-dialog-scrollable"
                    role="document"
                >
                    <div className="modal-content" style={modalStyles.wrap}>
                        <div className="modal-header" style={modalStyles.header}>
                            <div style={modalStyles.titleWrap}>
                                <span style={modalStyles.badge}>
                                    {t("addExistingSeasonTeamsModal.badge")}
                                </span>
                                <h4
                                    className="modal-title"
                                    style={modalStyles.title}
                                >
                                    {t("addExistingSeasonTeamsModal.title")}
                                </h4>
                            </div>

                            <button
                                type="button"
                                aria-label={t("addExistingSeasonTeamsModal.closeAria")}
                                style={modalStyles.closeBtn}
                                onClick={closeModal}
                                disabled={isSubmitting}
                                title={t("addExistingSeasonTeamsModal.closeTitle")}
                            >
                                <span
                                    aria-hidden="true"
                                    style={{ fontSize: 20, lineHeight: 1 }}
                                >
                                    &times;
                                </span>
                            </button>
                        </div>

                        <div className="modal-body" style={modalStyles.body}>
                            <div style={modalStyles.card}>
                                <div style={modalStyles.sectionTitle}>
                                    {t("addExistingSeasonTeamsModal.sectionTitle")}
                                </div>

                                <div style={modalStyles.filtersRow}>
                                    <div style={modalStyles.filterItem} className="col-6">
                                        <label
                                            style={{
                                                fontSize: 12.5,
                                                fontWeight: 800,
                                                color: "rgba(20,21,26,0.70)",
                                                margin: 0,
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: 6,
                                                cursor: isSubmitting
                                                    ? "not-allowed"
                                                    : "pointer",
                                            }}
                                            title={t("addExistingSeasonTeamsModal.includeArchivedDivisionsTitle")}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={includeInactive}
                                                onChange={handleIncludeInactiveChange}
                                                disabled={
                                                    peerDivisionsLoading ||
                                                    isSubmitting
                                                }
                                            />
                                            {t("addExistingSeasonTeamsModal.includeArchivedDivisions")}
                                        </label>
                                    </div>

                                    <div style={modalStyles.filterItem}>
                                        <label
                                            style={modalStyles.label}
                                            htmlFor={`${modalId}-sport-filter`}
                                        >
                                            {t("addExistingSeasonTeamsModal.sport")}
                                        </label>
                                        <select
                                            id={`${modalId}-sport-filter`}
                                            className="form-control"
                                            value={selectedSportKey}
                                            onChange={handleSportChange}
                                            style={modalStyles.input}
                                            disabled={
                                                peerDivisionsLoading ||
                                                isSubmitting
                                            }
                                        >
                                            {translatedSportsList.map((s) => (
                                                <option
                                                    key={s.key || "all"}
                                                    value={s.key}
                                                >
                                                    {s.value}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div
                                    className="form-group"
                                    style={{ marginBottom: 12 }}
                                >
                                    <label style={modalStyles.label}>
                                        {t("addExistingSeasonTeamsModal.sourceDivision")}
                                    </label>
                                    <select
                                        className="form-control"
                                        value={selectedDivisionId}
                                        onChange={handleDivisionChange}
                                        style={modalStyles.input}
                                        disabled={
                                            isSubmitting ||
                                            peerDivisionsLoading ||
                                            !peerDivisions.length
                                        }
                                    >
                                        {!selectedDivisionId && (
                                            <option value="">
                                                {peerDivisionsLoading
                                                    ? t("addExistingSeasonTeamsModal.loadingDivisions")
                                                    : peerDivisions.length
                                                        ? t("addExistingSeasonTeamsModal.selectDivision")
                                                        : t("addExistingSeasonTeamsModal.noDivisionsAvailable")}
                                            </option>
                                        )}

                                        {peerDivisions.map((division) => (
                                            <option
                                                key={division.id}
                                                value={division.id}
                                            >
                                                {division.name}
                                            </option>
                                        ))}
                                    </select>
                                    {peerDivisionsError && (
                                        <div
                                            style={{
                                                ...modalStyles.hint,
                                                color: "#b00020",
                                            }}
                                        >
                                            {t("addExistingSeasonTeamsModal.couldNotLoadDivisions")}
                                        </div>
                                    )}
                                </div>

                                <div
                                    className="form-group"
                                    style={{ marginBottom: 12 }}
                                >
                                    <label style={modalStyles.label}>
                                        {t("addExistingSeasonTeamsModal.sourceSeason")}
                                    </label>
                                    <select
                                        className="form-control"
                                        value={selectedSeasonId}
                                        onChange={handleSeasonChange}
                                        style={modalStyles.input}
                                        disabled={
                                            isSubmitting ||
                                            !seasons?.filter(
                                                (s) =>
                                                    String(s.id) !==
                                                    String(activeSeasonData?.id)
                                            ).length
                                        }
                                    >
                                        {!selectedSeasonId && (
                                            <option value="">
                                                {seasons?.length
                                                    ? t("addExistingSeasonTeamsModal.selectSeason")
                                                    : t("addExistingSeasonTeamsModal.noSeasonsAvailable")}
                                            </option>
                                        )}

                                        {(seasons || [])
                                            .filter(
                                                (s) =>
                                                    String(s.id) !==
                                                    String(activeSeasonData?.id)
                                            )
                                            .map((season) => (
                                                <option
                                                    key={season.id}
                                                    value={season.id}
                                                >
                                                    {season.name}
                                                    {season.startDate &&
                                                    season.endDate
                                                        ? ` (${season.startDate} – ${season.endDate})`
                                                        : ""}
                                                </option>
                                            ))}
                                    </select>
                                    <div style={modalStyles.hint}>
                                        {t("addExistingSeasonTeamsModal.sourceSeasonHint")}
                                    </div>
                                </div>

                                <div
                                    className="form-group"
                                    style={{ marginBottom: 0 }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            gap: 12,
                                            padding: "10px 12px",
                                            borderRadius: 14,
                                            border: "1px solid rgba(0,0,0,0.08)",
                                            background: "rgba(0,0,0,0.01)",
                                        }}
                                    >
                                        <div style={{ minWidth: 0 }}>
                                            <div
                                                style={{
                                                    fontSize: 13,
                                                    fontWeight: 900,
                                                    color: "#14151a",
                                                }}
                                            >
                                                {t("addExistingSeasonTeamsModal.keepPlayersTitle")}
                                            </div>
                                            <div style={modalStyles.hint}>
                                                {t("addExistingSeasonTeamsModal.keepPlayersHint")}{" "}
                                                <p>
                                                    {deactivateSource
                                                        ? t("addExistingSeasonTeamsModal.keepPlayersRequiredForMove")
                                                        : t("addExistingSeasonTeamsModal.keepPlayersWarning")}
                                                </p>
                                            </div>
                                        </div>

                                        <label
                                            style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: 10,
                                                margin: 0,
                                                cursor: isSubmitting
                                                    ? "not-allowed"
                                                    : "pointer",
                                            }}
                                            title={
                                                isSubmitting
                                                    ? t("addExistingSeasonTeamsModal.pleaseWaitTitle")
                                                    : t("addExistingSeasonTeamsModal.keepPlayersTitleAttr")
                                            }
                                        >
                                            <input
                                                type="checkbox"
                                                checked={effectiveKeepPlayers}
                                                onChange={(e) =>
                                                    setKeepPlayers(
                                                        e.target.checked
                                                    )
                                                }
                                                disabled={isSubmitting || deactivateSource}
                                            />
                                        </label>
                                    </div>
                                </div>

                                <div
                                    className="form-group"
                                    style={{ marginTop: 12, marginBottom: 0 }}
                                >
                                    <div
                                        style={{
                                            padding: "10px 12px",
                                            borderRadius: 14,
                                            border: "1px solid rgba(0,0,0,0.08)",
                                            background: "rgba(0,0,0,0.01)",
                                        }}
                                    >
                                        <div
                                            style={{
                                                fontSize: 13,
                                                fontWeight: 900,
                                                color: "#14151a",
                                                marginBottom: 8,
                                            }}
                                        >
                                            {t("addExistingSeasonTeamsModal.transferOptionsTitle")}
                                        </div>

                                        <div style={modalStyles.filtersRow}>
                                            <div style={modalStyles.filterItem}>
                                                <label
                                                    style={modalStyles.label}
                                                    htmlFor={`${modalId}-target-stage`}
                                                >
                                                    {t("addExistingSeasonTeamsModal.targetStage")}
                                                </label>
                                                <select
                                                    id={`${modalId}-target-stage`}
                                                    className="form-control"
                                                    value={targetStageId}
                                                    onChange={(e) => {
                                                        setTargetStageId(e.target.value);
                                                        setTargetGroupId("");
                                                    }}
                                                    style={modalStyles.input}
                                                    disabled={
                                                        isSubmitting ||
                                                        !targetStages.length
                                                    }
                                                >
                                                    {!targetStageId && (
                                                        <option value="">
                                                            {targetStages.length
                                                                ? t("addExistingSeasonTeamsModal.selectStage")
                                                                : t("addExistingSeasonTeamsModal.noStagesAvailable")}
                                                        </option>
                                                    )}
                                                    {targetStages.map((stage) => (
                                                        <option
                                                            key={stage.id}
                                                            value={stage.id}
                                                        >
                                                            {stage.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div style={modalStyles.filterItem}>
                                                <label
                                                    style={modalStyles.label}
                                                    htmlFor={`${modalId}-target-group`}
                                                >
                                                    {t("addExistingSeasonTeamsModal.targetGroup")}
                                                </label>
                                                <select
                                                    id={`${modalId}-target-group`}
                                                    className="form-control"
                                                    value={targetGroupId}
                                                    onChange={(e) =>
                                                        setTargetGroupId(e.target.value)
                                                    }
                                                    style={modalStyles.input}
                                                    disabled={
                                                        isSubmitting ||
                                                        !targetGroups.length
                                                    }
                                                >
                                                    <option value="">
                                                        {targetGroups.length
                                                            ? t("addExistingSeasonTeamsModal.selectGroupOptional")
                                                            : t("addExistingSeasonTeamsModal.noGroupsAvailable")}
                                                    </option>
                                                    {targetGroups.map((group) => (
                                                        <option
                                                            key={group.id}
                                                            value={group.id}
                                                        >
                                                            {group.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        <div
                                            style={{
                                                display: "grid",
                                                gap: 8,
                                                marginTop: 8,
                                            }}
                                        >
                                            <label
                                                style={{
                                                    display: "flex",
                                                    alignItems: "flex-start",
                                                    gap: 10,
                                                    margin: 0,
                                                    cursor: isSubmitting
                                                        ? "not-allowed"
                                                        : "pointer",
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={carryPoints}
                                                    onChange={(e) =>
                                                        setCarryPoints(e.target.checked)
                                                    }
                                                    disabled={isSubmitting}
                                                    style={{ marginTop: 3 }}
                                                />
                                                <span>
                                                    <span
                                                        style={{
                                                            display: "block",
                                                            fontSize: 13,
                                                            fontWeight: 900,
                                                            color: "#14151a",
                                                        }}
                                                    >
                                                        {t("addExistingSeasonTeamsModal.carryPointsTitle")}
                                                    </span>
                                                    <span style={modalStyles.hint}>
                                                        {t("addExistingSeasonTeamsModal.carryPointsHint")}
                                                    </span>
                                                </span>
                                            </label>

                                            {carryPoints && (
                                                <div style={modalStyles.filtersRow}>
                                                    <div style={modalStyles.filterItem}>
                                                        <label
                                                            style={modalStyles.label}
                                                            htmlFor={`${modalId}-source-stage`}
                                                        >
                                                            {t("addExistingSeasonTeamsModal.sourceStage")}
                                                        </label>
                                                        <select
                                                            id={`${modalId}-source-stage`}
                                                            className="form-control"
                                                            value={sourceStageId}
                                                            onChange={(e) => {
                                                                setSourceStageId(e.target.value);
                                                                setSourceGroupId("");
                                                            }}
                                                            style={modalStyles.input}
                                                            disabled={
                                                                isSubmitting ||
                                                                !sourceStages.length
                                                            }
                                                        >
                                                            {!sourceStageId && (
                                                                <option value="">
                                                                    {sourceStages.length
                                                                        ? t("addExistingSeasonTeamsModal.selectStage")
                                                                        : t("addExistingSeasonTeamsModal.noStagesAvailable")}
                                                                </option>
                                                            )}
                                                            {sourceStages.map((stage) => (
                                                                <option
                                                                    key={stage.id}
                                                                    value={stage.id}
                                                                >
                                                                    {stage.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    <div style={modalStyles.filterItem}>
                                                        <label
                                                            style={modalStyles.label}
                                                            htmlFor={`${modalId}-source-group`}
                                                        >
                                                            {t("addExistingSeasonTeamsModal.sourceGroup")}
                                                        </label>
                                                        <select
                                                            id={`${modalId}-source-group`}
                                                            className="form-control"
                                                            value={sourceGroupId}
                                                            onChange={(e) =>
                                                                setSourceGroupId(e.target.value)
                                                            }
                                                            style={modalStyles.input}
                                                            disabled={
                                                                isSubmitting ||
                                                                !sourceGroups.length
                                                            }
                                                        >
                                                            <option value="">
                                                                {sourceGroups.length
                                                                    ? t("addExistingSeasonTeamsModal.selectGroupOptional")
                                                                    : t("addExistingSeasonTeamsModal.noGroupsAvailable")}
                                                            </option>
                                                            {sourceGroups.map((group) => (
                                                                <option
                                                                    key={group.id}
                                                                    value={group.id}
                                                                >
                                                                    {group.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                            )}

                                            <label
                                                style={{
                                                    display: "flex",
                                                    alignItems: "flex-start",
                                                    gap: 10,
                                                    margin: 0,
                                                    cursor: isSubmitting
                                                        ? "not-allowed"
                                                        : "pointer",
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={deactivateSource}
                                                    onChange={(e) =>
                                                        setDeactivateSource(e.target.checked)
                                                    }
                                                    disabled={isSubmitting}
                                                    style={{ marginTop: 3 }}
                                                />
                                                <span>
                                                    <span
                                                        style={{
                                                            display: "block",
                                                            fontSize: 13,
                                                            fontWeight: 900,
                                                            color: "#14151a",
                                                        }}
                                                    >
                                                        {t("addExistingSeasonTeamsModal.deactivateSourceTitle")}
                                                    </span>
                                                    <span style={modalStyles.hint}>
                                                        {t("addExistingSeasonTeamsModal.deactivateSourceHint")}
                                                    </span>
                                                </span>
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div
                                    style={{
                                        borderRadius: 14,
                                        border: "1px solid rgba(0,0,0,0.06)",
                                        background: "#fff",
                                        overflow: "hidden",
                                        marginTop: 12,
                                    }}
                                >
                                    <div className="table-responsive">
                                        <table
                                            className="table mb-0"
                                            style={{ minWidth: 520 }}
                                        >
                                            <thead
                                                style={{
                                                    background: "rgba(0,0,0,0.02)",
                                                }}
                                            >
                                            <tr>
                                                <th style={{ width: 52 }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isAllSelected}
                                                        onChange={(e) =>
                                                            onSelectAll(
                                                                e.target.checked
                                                            )
                                                        }
                                                        disabled={
                                                            isSubmitting ||
                                                            !selectableTeamIds.length
                                                        }
                                                    />
                                                </th>
                                                <th>{t("addExistingSeasonTeamsModal.table.team")}</th>
                                                <th
                                                    style={{
                                                        width: 130,
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    {t("addExistingSeasonTeamsModal.table.players")}
                                                </th>
                                                <th
                                                    style={{
                                                        width: 160,
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    {t("addExistingSeasonTeamsModal.table.status")}
                                                </th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {!teams.length && (
                                                <tr>
                                                    <td
                                                        colSpan={4}
                                                        style={{
                                                            padding: 16,
                                                            textAlign: "center",
                                                        }}
                                                    >
                                                            <span
                                                                style={{
                                                                    color: "rgba(20,21,26,0.55)",
                                                                    fontWeight: 800,
                                                                }}
                                                            >
                                                                {selectedSeasonId
                                                                    ? t("addExistingSeasonTeamsModal.noTeamsForSeason")
                                                                    : t("addExistingSeasonTeamsModal.selectSeasonToSeeTeams")}
                                                            </span>
                                                    </td>
                                                </tr>
                                            )}

                                            {teams.map((seasonTeam) => {
                                                const teamId = seasonTeam.teamId;
                                                const alreadyInSeason =
                                                    existingTeamIds.has(
                                                        String(teamId)
                                                    );
                                                const logoSrc =
                                                    seasonTeam.logo ||
                                                    seasonTeam?.team?.logoUrl ||
                                                    null;
                                                const playersCount =
                                                    seasonTeam?.team?.players?.length || 0;
                                                const checked =
                                                    selectedTeamIds.includes(teamId);

                                                return (
                                                    <tr key={teamId}>
                                                        <td
                                                            style={{
                                                                verticalAlign: "middle",
                                                            }}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={checked}
                                                                disabled={
                                                                    isSubmitting ||
                                                                    alreadyInSeason
                                                                }
                                                                onChange={() =>
                                                                    toggleTeam(teamId)
                                                                }
                                                            />
                                                        </td>
                                                        <td
                                                            style={{
                                                                verticalAlign: "middle",
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    gap: 10,
                                                                    minWidth: 0,
                                                                }}
                                                            >
                                                                {logoSrc ? (
                                                                    <img
                                                                        src={logoSrc}
                                                                        alt="Team Logo"
                                                                        style={{
                                                                            width: 40,
                                                                            height: 40,
                                                                            borderRadius: 12,
                                                                            objectFit: "cover",
                                                                            border: "1px solid rgba(0,0,0,0.10)",
                                                                            background: "#fff",
                                                                            padding: 3,
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <InitialsAvatar
                                                                        name={seasonTeam.teamName}
                                                                        size={40}
                                                                        backgroundColor={colors.primary}
                                                                        textColor={colors.textOnPrimary}
                                                                    />
                                                                )}

                                                                <div
                                                                    style={{
                                                                        minWidth: 0,
                                                                    }}
                                                                >
                                                                    <div
                                                                        style={{
                                                                            fontSize: 14,
                                                                            fontWeight: 900,
                                                                            color: "#14151a",
                                                                        }}
                                                                    >
                                                                        {seasonTeam.teamName}
                                                                    </div>
                                                                    <div
                                                                        style={{
                                                                            fontSize: 12.5,
                                                                            fontWeight: 700,
                                                                            color: "rgba(20,21,26,0.60)",
                                                                        }}
                                                                    >
                                                                        {t("addExistingSeasonTeamsModal.seasonTeam")}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td
                                                            style={{
                                                                verticalAlign: "middle",
                                                                textAlign: "center",
                                                            }}
                                                        >
                                                                <span
                                                                    style={modalStyles.chip}
                                                                >
                                                                    <i className="fas fa-users" />
                                                                    {playersCount}
                                                                </span>
                                                        </td>
                                                        <td
                                                            style={{
                                                                verticalAlign: "middle",
                                                                textAlign: "center",
                                                                fontSize: 12,
                                                                fontWeight: 800,
                                                            }}
                                                        >
                                                            {alreadyInSeason ? (
                                                                <span
                                                                    style={{
                                                                        color: "rgba(20,21,26,0.55)",
                                                                    }}
                                                                >
                                                                        {t("addExistingSeasonTeamsModal.alreadyInSeason", {
                                                                            seasonName: activeSeasonData?.name,
                                                                        })}
                                                                    </span>
                                                            ) : checked ? (
                                                                <span
                                                                    style={{
                                                                        color: GlobalStyles.colors.woodsmoke,
                                                                    }}
                                                                >
                                                                        {t("addExistingSeasonTeamsModal.selected")}
                                                                    </span>
                                                            ) : (
                                                                <span
                                                                    style={{
                                                                        color: "rgba(20,21,26,0.60)",
                                                                    }}
                                                                >
                                                                        {t("addExistingSeasonTeamsModal.available")}
                                                                    </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer" style={modalStyles.footer}>
                            <span
                                style={{
                                    color: "rgba(20,21,26,0.60)",
                                    fontSize: 12.5,
                                    fontWeight: 800,
                                }}
                            >
                                {selectedTeamIds.length
                                    ? t("addExistingSeasonTeamsModal.teamsSelectedFooter", {
                                        count: selectedTeamIds.length,
                                    })
                                    : t("addExistingSeasonTeamsModal.selectTeamsFooter")}
                            </span>

                            <div style={{ display: "flex", gap: 8 }}>
                                <button
                                    type="button"
                                    className="btn"
                                    onClick={closeModal}
                                    style={{
                                        ...modalStyles.btn,
                                        ...modalStyles.btnGhost,
                                    }}
                                    disabled={isSubmitting}
                                >
                                    <i className="fas fa-times" />
                                    {t("addExistingSeasonTeamsModal.close")}
                                </button>

                                <button
                                    type="button"
                                    className="btn"
                                    style={{
                                        ...modalStyles.btn,
                                        ...modalStyles.btnPrimary,
                                    }}
                                    onClick={handleConfirm}
                                    disabled={
                                        isSubmitting ||
                                        !selectedSeasonId ||
                                        !selectedTeamIds.length
                                    }
                                >
                                    <i className="fas fa-check" />
                                    {isSubmitting
                                        ? t("addExistingSeasonTeamsModal.adding")
                                        : t("addExistingSeasonTeamsModal.addSelectedTeams")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
              #${modalId} {
                --kbola-canary: ${GlobalStyles.colors.canary};
                --kbola-woodsmoke: ${GlobalStyles.colors.woodsmoke};
              }
            `}</style>
        </>
    );
}
